import React, { useState, useEffect, useRef } from 'react';
import {
  AppSpec,
  Skill,
  Requirement,
  SteeringConfig,
  User,
  AVAILABLE_SKILLS,
  ICON_OPTIONS,
  SkillTemplate
} from '../types';
import {
  saveApp,
  getApp,
  listApps,
  fullDeploy,
  runWizard,
  WizardStepResult
} from '../services/api';
import { validateSpec, sanitizeSubdomain, WIZARD_STEP_LABELS, ValidationIssue } from '../utils/validation';
import { iconEmoji } from '../utils/icons';
import SkillSelector from './SkillSelector';
import RequirementEditor from './RequirementEditor';
import SteeringPanel from './SteeringPanel';
import AppPreview from './AppPreview';
import InfraStatusPanel from './InfraStatusPanel';
import '../styles/SpecDrivenPage.css';

interface Props {
  user: User;
  editingAppId: string | null;
  onBack: () => void;
}

type Step = 'basics' | 'skills' | 'requirements' | 'steering' | 'preview' | 'generate';

/** Planned wizard steps shown while the provisioning agent runs. */
const PLANNED_WIZARD_STEPS = [
  { key: 'register_app', icon: '📋' },
  { key: 'allocate_port', icon: '🔌' },
  { key: 'scaffold_app', icon: '🏗️' },
  { key: 'provision_s3', icon: '📦' },
  { key: 'provision_cloudfront', icon: '🌐' },
  { key: 'provision_cognito', icon: '🔐' },
  { key: 'provision_database', icon: '🗄️' },
  { key: 'provision_dns', icon: '🧭' },
  { key: 'security_check', icon: '🛡️' },
  { key: 'sdk_codegen', icon: '🤖' },
  { key: 'deploy_frontend', icon: '🚀' },
  { key: 'sdk_docs', icon: '📄' },
  { key: 'sdk_videos', icon: '🎬' },
  { key: 'github_push', icon: '🐙' },
];

const TEMPLATE_STEPS = [
  { key: 'save', label: 'Save specification', icon: '💾' },
  { key: 'generate', label: 'Generate code from templates', icon: '⚙️' },
  { key: 'deploy', label: 'Deploy to S3 + CloudFront', icon: '🚀' },
  { key: 'portal', label: 'Register in EA Portal', icon: '🏠' },
];

type GenPhase = 'idle' | 'saving' | 'running' | 'done' | 'error';

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
}

const SpecDrivenPage: React.FC<Props> = ({ user, editingAppId, onBack }) => {
  const [currentStep, setCurrentStep] = useState<Step>('basics');
  const [genPhase, setGenPhase] = useState<GenPhase>('idle');
  const [generationStatus, setGenerationStatus] = useState<string>('');
  const [stepResults, setStepResults] = useState<WizardStepResult[] | null>(null);
  const [wizardUrl, setWizardUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [existingSubdomains, setExistingSubdomains] = useState<string[]>([]);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');

  const [spec, setSpec] = useState<AppSpec>({
    id: '',
    name: '',
    subdomain: '',
    displayName: '',
    description: '',
    icon: 'code',
    skills: [],
    requirements: [],
    steering: {
      hasBackend: false,
      backendType: 'none',
      authRequired: true,
      allowedGroups: ['admin'],
      dataStorage: 'none',
      apiPrefix: '/api',
      chatEnabled: 'disabled',
      generationMethod: 'llm'
    },
    status: 'draft',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: user.username
  });

  useEffect(() => {
    if (editingAppId) {
      getApp(editingAppId)
        .then(data => setSpec(data))
        .catch(err => {
          console.error('Failed to load app spec:', err);
          setError('Failed to load the existing app spec — starting from a blank spec.');
        });
    }
  }, [editingAppId]);

  // Load existing subdomains once for collision validation
  useEffect(() => {
    listApps()
      .then(apps => setExistingSubdomains(apps.map(a => a.subdomain).filter(Boolean)))
      .catch(() => { /* validation simply skips collision check */ });
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const startTimer = () => {
    setElapsed(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const updateSpec = (updates: Partial<AppSpec>) => {
    setSpec(prev => ({
      ...prev,
      ...updates,
      updatedAt: new Date().toISOString()
    }));
  };

  const handleSubdomainChange = (value: string) => {
    const sanitized = sanitizeSubdomain(value);
    updateSpec({ subdomain: sanitized, name: sanitized });
  };

  const handleSkillToggle = (skillId: string, externalTemplate?: SkillTemplate) => {
    const template = externalTemplate || AVAILABLE_SKILLS.find(s => s.id === skillId);
    if (!template) return;

    const existingIndex = spec.skills.findIndex(s => s.id === skillId);
    if (existingIndex >= 0) {
      updateSpec({ skills: spec.skills.filter(s => s.id !== skillId) });
    } else {
      const newSkill: Skill = {
        id: template.id,
        name: template.name,
        description: template.description,
        category: template.category,
        enabled: true
      };
      const updates: Partial<AppSpec> = { skills: [...spec.skills, newSkill] };
      // Auto-enable backend when a backend skill is selected
      if (template.category === 'backend' && !spec.steering.hasBackend) {
        updates.steering = {
          ...spec.steering,
          hasBackend: true,
          backendType: 'nodejs',
          dataStorage: 's3'
        };
      }
      updateSpec(updates);
    }
  };

  const handleAddRequirement = (req: Omit<Requirement, 'id' | 'status'>) => {
    const newReq: Requirement = { ...req, id: `req-${Date.now()}`, status: 'draft' };
    updateSpec({ requirements: [...spec.requirements, newReq] });
  };

  const handleUpdateRequirement = (id: string, updates: Partial<Requirement>) => {
    updateSpec({
      requirements: spec.requirements.map(r => (r.id === id ? { ...r, ...updates } : r))
    });
  };

  const handleDeleteRequirement = (id: string) => {
    updateSpec({ requirements: spec.requirements.filter(r => r.id !== id) });
  };

  const handleSteeringChange = (updates: Partial<SteeringConfig>) => {
    updateSpec({ steering: { ...spec.steering, ...updates } });
  };

  const runValidation = (): ValidationIssue[] => {
    // Allow redeploys: exclude this app's own subdomain from the collision list
    const others = editingAppId
      ? existingSubdomains.filter(s => s !== spec.subdomain)
      : existingSubdomains;
    const found = validateSpec(spec, others);
    setIssues(found);
    return found;
  };

  const handleGenerate = async () => {
    const found = runValidation();
    if (found.length > 0) {
      setError(`Please fix ${found.length} issue${found.length > 1 ? 's' : ''} before generating.`);
      return;
    }

    const useLLM = spec.steering.generationMethod !== 'template';
    setGenPhase('saving');
    setError(null);
    setStepResults(null);
    setWizardUrl(null);
    setGenerationStatus('Saving specification…');
    startTimer();

    try {
      const savedSpec = await saveApp(spec);
      updateSpec({ id: savedSpec.id });

      if (useLLM) {
        setGenPhase('running');
        setGenerationStatus(
          'Provisioning Agent is building your app — infrastructure, AI code generation and deployment. ' +
          'This can take several minutes; keep this tab open.'
        );

        const requirementsText = spec.requirements
          .map(r => `[${r.priority.toUpperCase()}] ${r.title}: ${r.description}`)
          .join('\n');

        const wizardResult = await runWizard({
          appName: spec.subdomain || spec.name,
          description: spec.description,
          requirements: requirementsText,
          skills: spec.skills.map(s => s.name),
          llmModel: spec.steering.llmModel || 'qwen3:8b',
          backendServer: spec.steering.backendServer || 'azure',
          options: {
            skipDatabase: spec.steering.dataStorage !== 'mysql',
            skipScaffold: false,
            skipCodeGen: spec.steering.skipCodeGen,
            skipDeploy: false,
            skipDocs: spec.steering.skipDocs,
            skipVideos: spec.steering.skipVideos,
            skipGithub: spec.steering.skipGithub,
            sdkCapabilities: spec.steering.sdkEnabled
              ? {
                  chat: spec.steering.sdkChat || false,
                  crosscheck: spec.steering.sdkCrosscheck || false,
                  docgen: spec.steering.sdkDocgen || false,
                  llmBridge: spec.steering.sdkLlmBridge || false,
                }
              : { chat: spec.steering.chatEnabled !== 'disabled' },
          },
        });

        if (wizardResult.steps) setStepResults(wizardResult.steps);
        if (wizardResult.url) setWizardUrl(wizardResult.url);

        if (!wizardResult.success) {
          const failedSteps = wizardResult.steps
            ?.filter(s => s.status === 'failed')
            .map(s => `${WIZARD_STEP_LABELS[s.step] || s.step}: ${s.error}`)
            .join('; ');
          throw new Error(`Provisioning partially failed — ${failedSteps || wizardResult.summary || 'see step details'}`);
        }
      } else {
        setGenPhase('running');
        setGenerationStatus('Generating and deploying from templates…');
        await fullDeploy(savedSpec.id);
      }

      stopTimer();
      setGenPhase('done');
      setGenerationStatus('Your app has been created!');
      updateSpec({ status: 'deployed' });
    } catch (err) {
      stopTimer();
      console.error('Generation failed:', err);
      setGenPhase('error');
      setError(err instanceof Error ? err.message : 'Generation failed');
      updateSpec({ status: 'failed' });
    }
  };

  const handleSaveDraft = async () => {
    setSaveState('saving');
    try {
      const savedSpec = await saveApp({ ...spec, status: 'draft' });
      updateSpec({ id: savedSpec.id });
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2500);
    } catch (err) {
      console.error('Failed to save draft:', err);
      setSaveState('failed');
      setTimeout(() => setSaveState('idle'), 4000);
    }
  };

  const steps: { key: Step; label: string }[] = [
    { key: 'basics', label: 'Basic Info' },
    { key: 'skills', label: 'Skills' },
    { key: 'requirements', label: 'Requirements' },
    { key: 'steering', label: 'Steering' },
    { key: 'preview', label: 'Preview' },
    { key: 'generate', label: 'Generate' }
  ];

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);
  const isGenerating = genPhase === 'saving' || genPhase === 'running';

  const goToStep = (step: Step) => {
    if (isGenerating) return;
    setCurrentStep(step);
    setError(null);
  };

  const nextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) goToStep(steps[nextIndex].key);
  };

  const prevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) goToStep(steps[prevIndex].key);
  };

  /** Which planned steps apply, given the wizard skip options. */
  const activePlannedSteps = PLANNED_WIZARD_STEPS.filter(s => {
    if (s.key === 'provision_database' && spec.steering.dataStorage !== 'mysql') return false;
    if (s.key === 'sdk_codegen' && spec.steering.skipCodeGen) return false;
    if (s.key === 'sdk_docs' && spec.steering.skipDocs) return false;
    if (s.key === 'sdk_videos' && spec.steering.skipVideos) return false;
    if (s.key === 'github_push' && spec.steering.skipGithub) return false;
    return true;
  });

  const resultFor = (key: string): WizardStepResult | undefined =>
    stepResults?.find(r => r.step === key || r.step.replace(/-/g, '_') === key);

  const renderWizardProgress = () => (
    <div className="generating-status">
      <div className="generation-header">
        {isGenerating ? (
          <>
            <span className="big-spinner" />
            <div>
              <strong>Provisioning in progress</strong>
              <span className="elapsed">Elapsed: {formatElapsed(elapsed)}</span>
            </div>
          </>
        ) : genPhase === 'done' ? (
          <>
            <span className="done-icon">🎉</span>
            <div>
              <strong>App created successfully</strong>
              <span className="elapsed">Took {formatElapsed(elapsed)}</span>
            </div>
          </>
        ) : (
          <>
            <span className="done-icon">⚠️</span>
            <div>
              <strong>Provisioning finished with errors</strong>
              <span className="elapsed">After {formatElapsed(elapsed)}</span>
            </div>
          </>
        )}
      </div>

      <div className="generation-progress">
        {activePlannedSteps.map(step => {
          const result = resultFor(step.key);
          const state = result
            ? result.status
            : isGenerating
              ? 'pending'
              : 'skipped';
          return (
            <div key={step.key} className={`generation-step-item ${state}`}>
              <span className="step-icon">
                {state === 'success' ? '✅' : state === 'failed' ? '❌' : state === 'skipped' ? '⏭️' : step.icon}
              </span>
              <span className="step-label">{WIZARD_STEP_LABELS[step.key] || step.key}</span>
              {result?.status === 'failed' && result.error && (
                <span className="step-error" title={result.error}>{result.error}</span>
              )}
              {!result && isGenerating && <span className="step-spinner" />}
            </div>
          );
        })}
      </div>

      <p className="status-message">{generationStatus}</p>

      {genPhase === 'done' && (
        <div className="success-message">
          <p>
            🎉 <strong>{spec.displayName}</strong> is live at{' '}
            <a href={wizardUrl || `https://${spec.subdomain}.sae-g.com`} target="_blank" rel="noopener noreferrer">
              {wizardUrl || `https://${spec.subdomain}.sae-g.com`}
            </a>
          </p>
          <p className="hint">CloudFront/DNS propagation may take a few minutes on first deploy.</p>
          <button className="btn-primary" onClick={onBack}>← Back to My Apps</button>
        </div>
      )}

      {genPhase === 'error' && (
        <div className="generate-actions">
          <button className="btn-secondary" onClick={() => { setGenPhase('idle'); setError(null); }}>
            ← Adjust spec
          </button>
          <button className="btn-primary" onClick={handleGenerate}>↻ Retry</button>
        </div>
      )}
    </div>
  );

  const renderTemplateProgress = () => (
    <div className="generating-status">
      <div className="generation-header">
        {isGenerating ? <span className="big-spinner" /> : <span className="done-icon">{genPhase === 'done' ? '🎉' : '⚠️'}</span>}
        <div>
          <strong>{isGenerating ? 'Deploying from templates' : genPhase === 'done' ? 'App deployed' : 'Deployment failed'}</strong>
          <span className="elapsed">Elapsed: {formatElapsed(elapsed)}</span>
        </div>
      </div>
      <div className="generation-progress">
        {TEMPLATE_STEPS.map(step => (
          <div key={step.key} className={`generation-step-item ${genPhase === 'done' ? 'success' : 'pending'}`}>
            <span className="step-icon">{genPhase === 'done' ? '✅' : step.icon}</span>
            <span className="step-label">{step.label}</span>
            {isGenerating && <span className="step-spinner" />}
          </div>
        ))}
      </div>
      <p className="status-message">{generationStatus}</p>
      {genPhase === 'done' && (
        <div className="success-message">
          <p>
            🎉 Live at{' '}
            <a href={`https://${spec.subdomain}.sae-g.com`} target="_blank" rel="noopener noreferrer">
              https://{spec.subdomain}.sae-g.com
            </a>
          </p>
          <button className="btn-primary" onClick={onBack}>← Back to My Apps</button>
        </div>
      )}
      {genPhase === 'error' && (
        <div className="generate-actions">
          <button className="btn-secondary" onClick={() => { setGenPhase('idle'); setError(null); }}>← Adjust spec</button>
          <button className="btn-primary" onClick={handleGenerate}>↻ Retry</button>
        </div>
      )}
    </div>
  );

  return (
    <div className="spec-driven-page">
      <div className="page-header">
        <button className="back-button" onClick={onBack}>← Back to Apps</button>
        <h1>{editingAppId ? 'Edit & Redeploy App' : 'Create New App'}</h1>
        {editingAppId && spec.status === 'deployed' && (
          <div className="editing-info">
            <span className="editing-badge">Editing Deployed App</span>
            <a className="app-url" href={`https://${spec.subdomain}.sae-g.com`} target="_blank" rel="noopener noreferrer">
              https://{spec.subdomain}.sae-g.com
            </a>
          </div>
        )}
      </div>

      {/* Step Progress */}
      <div className="step-progress">
        {steps.map((step, index) => (
          <div
            key={step.key}
            className={`step ${currentStep === step.key ? 'active' : ''} ${index < currentStepIndex ? 'completed' : ''}`}
            onClick={() => goToStep(step.key)}
          >
            <span className="step-number">{index < currentStepIndex ? '✓' : index + 1}</span>
            <span className="step-label">{step.label}</span>
          </div>
        ))}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {issues.length > 0 && (
        <div className="issues-banner">
          <strong>Spec issues:</strong>
          <ul>
            {issues.map((iss, i) => (
              <li key={i}><span className="issue-field">{iss.field}</span> {iss.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="step-content">
        {currentStep === 'basics' && (
          <div className="basics-form">
            <h2>Basic Information</h2>
            <div className="form-group">
              <label>Subdomain *</label>
              <div className="subdomain-input">
                <input
                  type="text"
                  value={spec.subdomain}
                  onChange={e => handleSubdomainChange(e.target.value)}
                  placeholder="my-app"
                />
                <span className="subdomain-suffix">.sae-g.com</span>
              </div>
              <small>
                Your app will be served at https://{spec.subdomain || 'my-app'}.sae-g.com
                {existingSubdomains.includes(spec.subdomain) && !editingAppId && (
                  <span className="inline-warn"> — already in use!</span>
                )}
              </small>
            </div>

            <div className="form-group">
              <label>Display Name *</label>
              <input
                type="text"
                value={spec.displayName}
                onChange={e => updateSpec({ displayName: e.target.value })}
                placeholder="My Application"
              />
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={spec.description}
                onChange={e => updateSpec({ description: e.target.value })}
                placeholder="Describe what your app does — the AI uses this to generate your app, so be specific…"
                rows={4}
              />
              <small>{spec.description.length} characters {spec.description.length < 10 && '(minimum 10)'}</small>
            </div>

            <div className="form-group">
              <label>Icon</label>
              <div className="icon-selector">
                {ICON_OPTIONS.map(icon => (
                  <button
                    key={icon}
                    className={`icon-option ${spec.icon === icon ? 'selected' : ''}`}
                    onClick={() => updateSpec({ icon })}
                    title={icon}
                  >
                    {iconEmoji(icon)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {currentStep === 'skills' && (
          <SkillSelector selectedSkills={spec.skills} onSkillToggle={handleSkillToggle} />
        )}

        {currentStep === 'requirements' && (
          <RequirementEditor
            requirements={spec.requirements}
            onAdd={handleAddRequirement}
            onUpdate={handleUpdateRequirement}
            onDelete={handleDeleteRequirement}
          />
        )}

        {currentStep === 'steering' && (
          <SteeringPanel steering={spec.steering} onChange={handleSteeringChange} />
        )}

        {currentStep === 'preview' && <AppPreview spec={spec} />}

        {currentStep === 'generate' && (
          <div className="generate-step">
            <h2>{editingAppId && spec.status === 'deployed' ? '🔄 Update & Redeploy' : '🚀 Generate Application'}</h2>

            {editingAppId && spec.status === 'deployed' && genPhase === 'idle' && (
              <div className="redeploy-notice">
                <p><strong>You are updating an existing deployed app.</strong></p>
                <p>
                  Changes will be applied to:{' '}
                  <a href={`https://${spec.subdomain}.sae-g.com`} target="_blank" rel="noopener noreferrer">
                    https://{spec.subdomain}.sae-g.com
                  </a>
                </p>
              </div>
            )}

            {genPhase !== 'idle' ? (
              spec.steering.generationMethod === 'template' ? renderTemplateProgress() : renderWizardProgress()
            ) : (
              <>
                <div className="summary">
                  <h3>Summary</h3>
                  <ul>
                    <li><strong>App Name:</strong> {spec.displayName || '—'}</li>
                    <li><strong>URL:</strong> https://{spec.subdomain || '—'}.sae-g.com</li>
                    <li><strong>Skills:</strong> {spec.skills.length} selected</li>
                    <li><strong>Requirements:</strong> {spec.requirements.length} defined</li>
                    <li><strong>Backend:</strong> {spec.steering.hasBackend ? `${spec.steering.backendType} on ${spec.steering.backendServer || 'azure'}` : 'None'}</li>
                    <li><strong>Auth Required:</strong> {spec.steering.authRequired ? `Yes (${spec.steering.allowedGroups.join(', ')})` : 'No'}</li>
                    <li>
                      <strong>Generation:</strong>{' '}
                      {spec.steering.generationMethod === 'template' ? 'Template-Based' : '🤖 LLM-Powered (Provisioning Agent)'}
                    </li>
                    {spec.steering.chatEnabled && spec.steering.chatEnabled !== 'disabled' && (
                      <li><strong>Chat Assistant:</strong> {spec.steering.chatEnabled === 'all' ? 'All Users' : 'Admin Only'}</li>
                    )}
                    {spec.steering.sdkEnabled && (
                      <li><strong>SDK:</strong> {[
                        spec.steering.sdkChat && 'Chat',
                        spec.steering.sdkCrosscheck && 'CrossCheck',
                        spec.steering.sdkDocgen && 'DocGen',
                        spec.steering.sdkLlmBridge && 'LLM Bridge',
                      ].filter(Boolean).join(', ') || 'Enabled'}</li>
                    )}
                    {spec.steering.llmModel && (spec.steering.sdkChat || spec.steering.sdkLlmBridge) && (
                      <li><strong>LLM Model:</strong> {spec.steering.llmModel}</li>
                    )}
                    {spec.steering.generationMethod === 'llm' && (
                      <li><strong>Wizard:</strong> {[
                        !spec.steering.skipCodeGen && 'Code Gen',
                        !spec.steering.skipDocs && 'Docs',
                        !spec.steering.skipVideos && 'Videos',
                        !spec.steering.skipGithub && 'GitHub',
                      ].filter(Boolean).join(' + ') || 'Infra only'}</li>
                    )}
                  </ul>
                </div>

                {!user.isAdmin && spec.steering.generationMethod !== 'template' && (
                  <div className="warn-box">
                    ⚠️ LLM-powered provisioning requires the <strong>admin</strong> group. Your account
                    ({user.username}) is not in it — the wizard call will be rejected. Ask an administrator
                    or switch to Template-Based generation in Steering.
                  </div>
                )}

                <div className="generate-actions">
                  <button className="btn-secondary" onClick={handleSaveDraft} disabled={isGenerating || saveState === 'saving'}>
                    {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? '✓ Saved' : saveState === 'failed' ? 'Save failed — retry' : 'Save as Draft'}
                  </button>
                  <button className="btn-primary generate-btn" onClick={handleGenerate} disabled={isGenerating}>
                    {editingAppId && spec.status === 'deployed'
                      ? '🔄 Update & Redeploy'
                      : spec.steering.generationMethod === 'template'
                        ? '🚀 Generate App'
                        : '🤖 Generate with AI'}
                  </button>
                </div>

                <div className="generate-info">
                  <p>This will:</p>
                  <ol>
                    {spec.steering.generationMethod !== 'template' && (
                      <li><strong>🤖 Use AI to generate functional React code</strong> based on your skills and requirements</li>
                    )}
                    {editingAppId && spec.status === 'deployed' ? (
                      <>
                        <li><strong>Regenerate code</strong> with your updated specification</li>
                        <li><strong>Overwrite existing files</strong> in the S3 bucket</li>
                        <li><strong>Invalidate CloudFront cache</strong> so changes appear immediately</li>
                        <li>Keep existing infrastructure (S3, CloudFront, DNS)</li>
                      </>
                    ) : (
                      <>
                        <li>Create an S3 bucket + CloudFront distribution</li>
                        <li>Configure the domain {spec.subdomain || 'my-app'}.sae-g.com</li>
                        {spec.steering.authRequired && <li>Create a Cognito auth client + login page</li>}
                        {spec.steering.dataStorage === 'mysql' && <li>Create MySQL database <code>{spec.steering.mysqlDbName}</code> on RDS</li>}
                        <li>Add a card to the EA Portal</li>
                      </>
                    )}
                    {spec.steering.chatEnabled && spec.steering.chatEnabled !== 'disabled' && (
                      <li>Include a chat panel for AI-powered feature requests</li>
                    )}
                    {spec.steering.generationMethod === 'llm' && !spec.steering.skipDocs && (
                      <li>Generate 10 documentation deliverables (EN/JP: MD, PPT, TCS PPT, PDF)</li>
                    )}
                    {spec.steering.generationMethod === 'llm' && !spec.steering.skipVideos && (
                      <li>Generate demo videos (Japanese + English, 60–120s each)</li>
                    )}
                    {spec.steering.generationMethod === 'llm' && !spec.steering.skipGithub && (
                      <li>Push to a private GitHub repository</li>
                    )}
                  </ol>
                </div>

                {spec.steering.generationMethod !== 'template' && (
                  <div className="llm-info-box">
                    <p><strong>💡 LLM-Powered Generation (Provisioning Agent)</strong></p>
                    <p>
                      The Provisioning Agent orchestrates everything: infrastructure provisioning (S3, CloudFront,
                      Cognito, Database, DNS), code generation via Claude Code SDK, documentation, demo videos,
                      and GitHub push — all from a single wizard call.
                    </p>
                    {spec.steering.sdkEnabled && (
                      <p style={{ marginTop: '0.5rem' }}>
                        <strong>SDK Capabilities:</strong> Your app will include{' '}
                        {[
                          spec.steering.sdkChat && 'an AI chat panel',
                          spec.steering.sdkCrosscheck && 'cross-check validation',
                          spec.steering.sdkDocgen && 'document generation',
                          spec.steering.sdkLlmBridge && 'LLM bridge',
                        ].filter(Boolean).join(', ')}.
                        {(spec.steering.sdkChat || spec.steering.sdkLlmBridge) &&
                          ` LLM: ${spec.steering.llmModel || 'qwen3:8b'} on Ollama.`}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <InfraStatusPanel />

      {/* Navigation Buttons */}
      <div className="step-navigation">
        <button className="btn-secondary" onClick={prevStep} disabled={currentStepIndex === 0 || isGenerating}>
          ← Previous
        </button>
        <span className="step-indicator">{currentStepIndex + 1} / {steps.length}</span>
        <button className="btn-primary" onClick={nextStep} disabled={currentStepIndex === steps.length - 1 || isGenerating}>
          Next →
        </button>
      </div>
    </div>
  );
};

export default SpecDrivenPage;
